import React, { useCallback, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import t from '@/utils/translate';

import LinearProgress from '@mui/material/LinearProgress';
import { AdminedProject } from '@/admin/types/admined-project.dto';
import Circle from '@/components/icons/Circle';
import Share from '@/components/icons/Share';

import Toolbar from '@/components/Toolbar';
import FilterTabsBar from '@/components/FilterTabsBar';
import FilterTabButton from '@/components/FilterTabButton';
import SearchInput from '@/components/SearchInput';
import TableList, { ColumnMeta } from '@/components/TableList';
import ViewModeSwitcher from '@/components/ViewModeSwitcher';

import reducer, { ROW_HEIGHT } from './reducer';
import ProjectCard from '@/admin/components/ProjectCard';
import UserCard from '@/components/UserCard';
import DateCard from '@/components/DateCard';
import { Link } from '@/components';
import Memory from '@/components/Memory';
import { ProjectDeletionMode, ProjectStatus } from '@bimdata/definitions/const';
import BulkOperationButton from '@/components/BulkOperationButton';
import { AmountsLabel } from '@/components/AmountLabel';
import TrashBasket from '@/components/icons/TrashBasket';
import deleteProjects from '@/admin/actions/delete-projects.action';
import { getProjects } from '@/admin/api/common';
import { handleError } from '@/actions/common';

const ShareIcon = styled(Share)`
  width: 24px;
  height: 24px;
  color: #C1C7D0;
`;

const idGetter = (ap: AdminedProject) => ap.code;

type BulkDeleteButtonsProps = {
  selected: AdminedProject[],
  onFullDelete: (ids: Set<number>) => void,
  onPartialDelete: (ids: Set<number>) => void,
};

const BulkDeleteButtons = ({ selected, onFullDelete, onPartialDelete }: BulkDeleteButtonsProps) => {
  const deleteHandler = useCallback(async (mode: ProjectDeletionMode) => {
    if (!window.confirm('Точно безвозвратно удалить данные в выбранных проектах?')) return;
    const ids = await deleteProjects(selected, mode);
    if (ids.size) {
      if (mode === ProjectDeletionMode.FULL) {
        return onFullDelete(ids);
      }
      onPartialDelete(ids);
    }
  }, [ selected, onFullDelete, onPartialDelete ]);
  
  const fullDeleteHandler = useCallback(
    async () => deleteHandler(ProjectDeletionMode.FULL),
    [ deleteHandler ]
  );

  const databaseDeleteHandler = useCallback(
    async () => deleteHandler(ProjectDeletionMode.DATABASE),
    [ deleteHandler ]
  );
  const sourceDeleteHandler = useCallback(
    async () => deleteHandler(ProjectDeletionMode.SOURCE),
    [ deleteHandler ]
  );
  const archiveDeleteHandler = useCallback(
    async () => deleteHandler(ProjectDeletionMode.ARCHIVE),
    [ deleteHandler ]
  );
  const filesDeleteHandler = useCallback(
    async () => deleteHandler(ProjectDeletionMode.FILES),
    [ deleteHandler ]
  );

  return <>
    <BulkOperationButton onClick={fullDeleteHandler} disabled={!selected.length}>
      <TrashBasket size={20} color="#DE350B"/>
      {t('admin.project.bulk.delete.full')}
      {!!selected.length && <AmountsLabel>{selected.length}</AmountsLabel>}
    </BulkOperationButton>

    <BulkOperationButton onClick={databaseDeleteHandler} disabled={!selected.length}>
      <TrashBasket size={20} color="#DE350B"/>
      {t('admin.project.bulk.delete.database')}
      {!!selected.length && <AmountsLabel>{selected.length}</AmountsLabel>}
    </BulkOperationButton>

    <BulkOperationButton onClick={sourceDeleteHandler} disabled={!selected.length}>
      <TrashBasket size={20} color="#DE350B"/>
      {t('admin.project.bulk.delete.source')}
      {!!selected.length && <AmountsLabel>{selected.length}</AmountsLabel>}
    </BulkOperationButton>

    <BulkOperationButton onClick={archiveDeleteHandler} disabled={!selected.length}>
      <TrashBasket size={20} color="#DE350B"/>
      {t('admin.project.bulk.delete.archive')}
      {!!selected.length && <AmountsLabel>{selected.length}</AmountsLabel>}
    </BulkOperationButton>

    <BulkOperationButton onClick={filesDeleteHandler} disabled={!selected.length}>
      <TrashBasket size={20} color="#DE350B"/>
      {t('admin.project.bulk.delete.files')}
      {!!selected.length && <AmountsLabel>{selected.length}</AmountsLabel>}
    </BulkOperationButton>
  </>
};

export default () => {
  const [state, dispatch] = reducer();
  const [selected, setSelected] = useState<AdminedProject[]>([]);

  const setArchivedFilter = useCallback((value: boolean | null) => {
    dispatch({ type: 'setArchivedFilter', payload: value });
  }, [ dispatch ]);
  const onSearchChange = useCallback((value: string) => {
    dispatch({ type: 'setSearch', payload: value });
  }, [ dispatch ]);

  const sortingHandler = useCallback((sortBy: keyof AdminedProject, sortOrder: 'A' | 'D') => {
    dispatch({ type: 'setSorting', payload: [sortBy, sortOrder] });
  }, [ dispatch ]);

  const loadNextPage = useCallback(async (offset: number, size: number) => {
    dispatch({ type: 'loadWithOffset', payload: offset });
  }, [ dispatch ]);

  const ensureRow = useCallback((item: AdminedProject) => {
    dispatch({
      type: 'ensure',
      payload: item
    });
  }, [ dispatch ]);

  const onTableListResize = useCallback((e: HTMLElement) => {
    dispatch({
      type: 'setPageSize',
      payload: e.getBoundingClientRect().height * 1.5 / ROW_HEIGHT,
    });
  }, [dispatch]);

  const rowStyle = useCallback((item: AdminedProject) => {
    if (state.params.archivedFilter === null && item.status === ProjectStatus.ARCH) {
      return {
        backgroundColor: '#eee',
        opacity:.5,
      };
    }
    return undefined;
  }, [state.params.archivedFilter]);

  const fullDeleteHandler = useCallback((ids: Set<number>) => {
    dispatch({ type: 'remove', payload: ids });
  }, [ dispatch ]);
  
  const partialDeleteHandler = useCallback(async (ids: Set<number>) => {
    try {
      const { page } = await getProjects({
        idsFilter: Array.from(ids),
        stats: false,
        total: false,
      });
      dispatch({ type: 'resetItems', payload: page });
    } catch (error) {
      handleError(error);
    }
  }, [ dispatch ]);

  const columns = useMemo<ColumnMeta[]>(() => ([
    {
      attrName: 'name',
      title: t('admin.project.column.name'),
      dataGetter: (item: AdminedProject) => (<ProjectCard project={item} logo={state.logos[item.id]} />),
    },
    {
      attrName:'owner',
      title: t('admin.project.column.owner'),
      dataGetter: (item: AdminedProject) => (<UserCard profile={item.owner} avatar={state.ownerAvatars[item.owner.id]} />),
    },
    {
      width: 60,
      title: t('admin.project.column.share'),
      dataGetter: (item: AdminedProject) => item.share ? (<Link to={`/sp/${item.share}`} target="_blank"><ShareIcon /></Link>) : (<></>),
    },
    {
      width: 60,
      attrName: 'participants',
      title: t('admin.project.column.participants'),
      defaultSorting: 'D',
    },
    {
      width: 60,
      attrName: 'models',
      title: t('admin.project.column.models'),
      defaultSorting: 'D',
    },
    {
      width: 80,
      attrName: 'dbSize',
      title: t('admin.project.column.db'),
      dataGetter: (item: AdminedProject) => item.dbSize && <Memory value={item.dbSize}/>,
      defaultSorting: 'D',
    },
    {
      width: 80,
      attrName: 'ssdSize',
      title: t('admin.project.column.ssd'),
      dataGetter: (item: AdminedProject) => item.ssdSize && <Memory value={item.ssdSize}/>,
      defaultSorting: 'D',
    },
    {
      width: 80,
      attrName: 'hddSize',
      title: t('admin.project.column.hdd'),
      dataGetter: (item: AdminedProject) => item.hddSize && <Memory value={item.hddSize}/>,
      defaultSorting: 'D',
    },
    ...(state.params.archivedFilter ? [
      {
        width: 130,
        attrName: 'archived',
        style: { textAlign: 'right' },
        title: t('admin.project.column.deleted'),
        dataGetter: (item: AdminedProject) => (item.archived && <DateCard value={item.archived} />),
        defaultSorting: 'D',
      } as ColumnMeta
    ] : []),
    {
      width: 130,
      attrName: 'updated',
      style: { textAlign: 'right' },
      title: t('admin.project.column.updated'),
      dataGetter: (item: AdminedProject) => (item.updated && <DateCard value={item.updated} />),
      defaultSorting: 'D',
    },
    {
      width: 130,
      attrName: 'created',
      style: { textAlign: 'right' },
      title: t('admin.project.column.created'),
      dataGetter: (item: AdminedProject) => (item.created && <DateCard value={item.created} />),
      defaultSorting: 'D',
    },
    {
      width: 28,
      style: { textAlign: 'center' },
      title: (<Circle size={10} stroke="#C1C7D0" fill="transparent"/>),
      dataGetter: (item: AdminedProject) => (
        <Circle
          size={10}
          stroke={(item.status === ProjectStatus.ARCH || !item.models) ? "#C1C7D0" : (item.hasError ? "#BF2600" : "#00875A")}
          fill="transparent"
        />
      ),
    },
    { width: 36 }
  ]), [ state.list, state.logos, state.ownerAvatars, state.params.archivedFilter]);

  return <>
    <Toolbar>
      <FilterTabsBar>
        <FilterTabButton
          value={null}
          filter={state.params.archivedFilter}
          onClick={setArchivedFilter}
          score={state.stats.total}
        >
          {t('admin.project.filter.all')}
        </FilterTabButton>
        <FilterTabButton
          value={false}
          filter={state.params.archivedFilter}
          onClick={setArchivedFilter}
          score={state.stats.active}
        >
          {t('admin.project.filter.active')}
        </FilterTabButton>
        <FilterTabButton
          value={true}
          filter={state.params.archivedFilter}
          onClick={setArchivedFilter}
          score={state.stats.archived}
        >
          {t('admin.project.filter.deleted')}
        </FilterTabButton>
      </FilterTabsBar>
      <ViewModeSwitcher currentMode={state.params.viewMode} />
      <SearchInput
        onChange={onSearchChange}
        value={state.params.search}
        placeholder={t('action.search')}
      />
    </Toolbar>
    {!state.list && <LinearProgress />}
    {state.list && (
      <TableList<AdminedProject>
        nextPageThreshold={0.1}
        sorting={state.params.sorting}
        columns={columns}
        data={(state.listSize !== null) ? state.list : undefined}
        onViewportResize={onTableListResize}
        pageSize={state.pageSize}
        onDataRequest={state.pageSize ? loadNextPage : undefined}
        onRowVisible={ensureRow}
        onSortingChange={sortingHandler}
        rowStyle={rowStyle}
        idGetter={idGetter}
        isCheckList={true}
        checkListChange={setSelected}
        selectionStatusCaption={t('admin.project.selectionCaption')}
      >
        <BulkDeleteButtons
          selected={selected}
          onPartialDelete={partialDeleteHandler}
          onFullDelete={fullDeleteHandler}
        />
      </TableList>
    )}
  </>
};